import { useEffect } from "react";
import { createPortal } from "react-dom";

import { OpenIncidentForm } from "@/components/qc/OpenIncidentForm";
import { hubModalOverlayProps } from "@/components/ui/HubModalOverlay";
import { HoverHintWithGlyph } from "@/components/ui/title-help";
import type {
  QcIncident,
  QcIncidentPcbaProductFamily,
  QcIncidentPcbaTlaVariant,
} from "@/pages/manufacturing/qcIncidentsApi";

export function ConfigurationPreConfigQcIncidentModal({
  open,
  onClose,
  onSubmitted,
  pcbaSerialPrefill,
  defaultPcbaProductFamily,
  defaultPcbaTlaVariant,
  defaultIssueNotes,
  targetSku,
  identityPrefill,
}: {
  open: boolean;
  onClose: () => void;
  /** Called after a successful open-incident submit and the modal closes (for example return focus to the scanner). */
  onSubmitted?: () => void;
  pcbaSerialPrefill: string;
  defaultPcbaProductFamily: QcIncidentPcbaProductFamily | "";
  defaultPcbaTlaVariant: QcIncidentPcbaTlaVariant | "";
  defaultIssueNotes: string;
  targetSku: string;
  identityPrefill?: {
    deviceLabelSerial?: string;
    clampSerial?: string;
    mcuUuid?: string;
  };
}): JSX.Element | null {
  useEffect(() => {
    if (!open) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  const handleSuccess = (_incident: QcIncident): void => {
    onClose();
    onSubmitted?.();
  };

  return createPortal(
    <div
      {...hubModalOverlayProps(false)}
      role="dialog"
      aria-modal="true"
      aria-labelledby="configuration-pre-config-qc-incident-title"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="my-4 w-full max-w-lg touch-manipulation rounded-lg border border-slate-200 bg-white shadow-xl"
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <div className="border-b border-slate-200 px-4 py-3 sm:px-5">
          <div className="flex items-center gap-2">
            <h2 id="configuration-pre-config-qc-incident-title" className="text-lg font-semibold text-slate-900">
              Pre-configuration QC incident
            </h2>
            <HoverHintWithGlyph
              hoverHint={
                "PCBA-directed incident before configure completes. Hub saves the PCBA serial plus any device label, clamp serial, or MCU UUID already collected on this form."
              }
            />
          </div>
          {targetSku.trim() !== "" ? (
            <p className="mt-1 text-xs text-slate-500">Target SKU {targetSku}</p>
          ) : null}
        </div>
        <div className="px-4 py-4 sm:px-5">
          <OpenIncidentForm
            fixedType="pcba"
            defaultType="pcba"
            serialPrefill={pcbaSerialPrefill}
            defaultPcbaProductFamily={defaultPcbaProductFamily}
            defaultPcbaTlaVariant={defaultPcbaTlaVariant}
            defaultIssue="Pre-configuration PCBA issue"
            defaultIssueNotes={defaultIssueNotes}
            identityPrefill={identityPrefill}
            onCancel={onClose}
            onSuccess={handleSuccess}
          />
        </div>
      </div>
    </div>,
    document.body,
  );
}
